import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MilestoneCard = ({ milestone, onEdit, onMarkDone, onDelete }) => {
  const getCategoryIcon = (category) => {
    const icons = {
      HYPOTHEQUE: 'Home',
      ASSURANCE: 'Shield',
      CHAUFFE_EAU: 'Droplet',
      CERTIFICAT_LOCALISATION: 'MapPin',
      INSPECTION: 'FileCheck',
      AUTRE: 'Calendar'
    };
    return icons[category] || 'Calendar';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      HYPOTHEQUE: 'Hypothèque',
      ASSURANCE: 'Assurance',
      CHAUFFE_EAU: 'Chauffe-eau',
      CERTIFICAT_LOCALISATION: 'Certificat localisation',
      INSPECTION: 'Inspection',
      AUTRE: 'Autre'
    };
    return labels[category] || category;
  };

  const getStatusColor = (status) => {
    const colors = {
      UPCOMING: 'var(--color-primary)',
      DONE: 'var(--color-success)',
      OVERDUE: 'var(--color-destructive)'
    };
    return colors[status] || 'var(--color-muted-foreground)';
  };

  const getStatusLabel = (status) => {
    const labels = {
      UPCOMING: 'À venir',
      DONE: 'Terminé',
      OVERDUE: 'En retard'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntil = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const daysUntil = getDaysUntil(milestone?.due_date);
  const isOverdue = daysUntil < 0 && milestone?.status !== 'DONE';

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--spacing-md)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--spacing-sm)',
      borderLeft: `4px solid ${getStatusColor(milestone?.status)}`
    }}>
      <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 'var(--spacing-sm)' }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--spacing-sm)', flex: 1 }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-primary)/10',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Icon name={getCategoryIcon(milestone?.category)} size={20} color="var(--color-primary)" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--color-foreground)',
              margin: 0,
              marginBottom: '4px'
            }}>
              {milestone?.name}
            </h3>
            <p style={{
              fontFamily: 'var(--font-caption)',
              fontSize: '0.75rem',
              color: 'var(--color-muted-foreground)',
              margin: 0
            }}>
              {getCategoryLabel(milestone?.category)}
            </p>
          </div>
        </div>
        <span style={{
          fontFamily: 'var(--font-caption)',
          fontSize: '0.75rem',
          fontWeight: 600,
          color: getStatusColor(milestone?.status),
          padding: '4px 8px',
          background: `${getStatusColor(milestone?.status)}/10`,
          borderRadius: 'var(--radius-sm)',
          whiteSpace: 'nowrap'
        }}>
          {getStatusLabel(milestone?.status)}
        </span>
      </div>

      <div style={{
        padding: 'var(--spacing-sm)',
        background: 'var(--color-muted)',
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <p style={{
            fontFamily: 'var(--font-caption)',
            fontSize: '0.75rem',
            color: 'var(--color-muted-foreground)',
            margin: 0,
            marginBottom: '2px'
          }}>
            Échéance
          </p>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'var(--color-foreground)',
            margin: 0
          }}>
            {formatDate(milestone?.due_date)}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{
            fontFamily: 'var(--font-caption)',
            fontSize: '0.75rem',
            color: 'var(--color-muted-foreground)',
            margin: 0,
            marginBottom: '2px'
          }}>
            {isOverdue ? 'En retard' : 'Dans'}
          </p>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: isOverdue ? 'var(--color-destructive)' : 'var(--color-foreground)',
            margin: 0
          }}>
            {isOverdue ? `${Math.abs(daysUntil)} jours` : `${daysUntil} jours`}
          </p>
        </div>
      </div>

      {milestone?.amount && (
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.875rem',
          color: 'var(--color-foreground)',
          margin: 0
        }}>
          <strong>Montant:</strong> {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(milestone.amount)}
        </p>
      )}

      {milestone?.meta && Object.keys(milestone.meta).length > 0 && (
        <div style={{
          padding: 'var(--spacing-xs)',
          background: 'var(--color-muted)',
          borderRadius: 'var(--radius-sm)',
          fontSize: '0.75rem',
          color: 'var(--color-muted-foreground)',
          fontFamily: 'var(--font-caption)'
        }}>
          {milestone.category === 'HYPOTHEQUE' && milestone.meta.lender && (
            <p style={{ margin: '2px 0' }}>Prêteur: {milestone.meta.lender}</p>
          )}
          {milestone.category === 'ASSURANCE' && milestone.meta.insurer && (
            <p style={{ margin: '2px 0' }}>Assureur: {milestone.meta.insurer}</p>
          )}
        </div>
      )}

      {milestone?.status !== 'DONE' && (
        <div style={{ display: 'flex', gap: 'var(--spacing-xs)', marginTop: 'var(--spacing-xs)' }}>
          <Button
            variant="outline"
            size="sm"
            iconName="Check"
            onClick={() => onMarkDone?.(milestone)}
            style={{ flex: 1 }}
          >
            Marquer terminé
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            onClick={() => onEdit?.(milestone)}
          />
          <Button
            variant="outline"
            size="sm"
            iconName="Trash2"
            onClick={() => onDelete?.(milestone)}
            style={{ color: 'var(--color-destructive)' }}
          />
        </div>
      )}

      {milestone?.status === 'DONE' && (
        <div style={{ display: 'flex', gap: 'var(--spacing-xs)', marginTop: 'var(--spacing-xs)' }}>
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            onClick={() => onEdit?.(milestone)}
            style={{ flex: 1 }}
          >
            Modifier
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Trash2"
            onClick={() => onDelete?.(milestone)}
            style={{ color: 'var(--color-destructive)' }}
          />
        </div>
      )}
    </div>
  );
};

export default MilestoneCard;
